import { useEffect, useRef, useState } from 'react'
import { createPortal } from 'react-dom'
import './CodeNav.css'

// Drill-in chevron (segmented mode / entering a standard).
const IconForward = () => (
  <svg
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    <polyline points="9 6 15 12 9 18" />
  </svg>
)

// Expand/collapse chevron (collapsed mode). Points down when the node is
// expanded (children shown below) and up when it is collapsed.
const IconChevron = ({ up }) => (
  <svg
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    {up ? <polyline points="6 15 12 9 18 15" /> : <polyline points="6 9 12 15 18 9" />}
  </svg>
)

// Bookmark tag: hollow when not bookmarked, filled when it is.
const IconBookmark = ({ filled }) => (
  <svg
    viewBox="0 0 24 24"
    fill={filled ? 'currentColor' : 'none'}
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    <path d="M19 21l-7-5-7 5V5a2 2 0 0 1 2-2h10a2 2 0 0 1 2 2z" />
  </svg>
)

// Hover box for a set bookmark: the topics it is filed under. Placed beside the
// icon with fixed positioning so the scrolling nav list does not clip it.
function BookmarkButton({ bookmarked, topics, onClick }) {
  const btnRef = useRef(null)
  const [tip, setTip] = useState(null)

  const showTip = () => {
    if (!bookmarked || !btnRef.current) return
    const rect = btnRef.current.getBoundingClientRect()
    const height = Math.min(280, ((topics?.length || 1) * 22) + 16)
    const rawTop = rect.top + rect.height / 2
    const top = Math.max(8 + height / 2, Math.min(rawTop, window.innerHeight - 8 - height / 2))
    const placeRight = rect.left < 200
    setTip({
      top,
      left: placeRight ? rect.right + 8 : rect.left - 8,
      placeRight,
    })
  }

  return (
    <>
      <button
        ref={btnRef}
        type="button"
        className={'code-nav-bookmark-btn' + (bookmarked ? ' is-on' : '')}
        title={bookmarked ? undefined : 'Bookmark'}
        aria-label={bookmarked ? 'Edit bookmark' : 'Bookmark'}
        aria-pressed={bookmarked}
        onMouseEnter={showTip}
        onMouseLeave={() => setTip(null)}
        onFocus={showTip}
        onBlur={() => setTip(null)}
        onClick={(event) => {
          event.stopPropagation()
          onClick?.()
        }}
      >
        <IconBookmark filled={bookmarked} />
      </button>
      {tip
        ? createPortal(
            <div
              className={'code-nav-bookmark-tip' + (tip.placeRight ? ' is-right' : '')}
              style={{ top: tip.top, left: tip.left }}
              role="tooltip"
            >
              {topics?.length ? (
                topics.map((topic) => (
                  <div className="code-nav-bookmark-tip-topic" key={topic.id}>
                    {topic.label}
                  </div>
                ))
              ) : (
                <div className="code-nav-bookmark-tip-empty">No topics</div>
              )}
            </div>,
            document.body
          )
        : null}
    </>
  )
}

// The Code (Library) nav list: a four-column table (label | page | bookmark |
// forward arrow) driven by the active window's view from useCodeWorkspace. It is
// presentational — the browse state (which level we're on, the trail) lives in
// the hook so the location breadcrumb can share it.
//
//   atRoot          true when `rows` are standards (page/bookmark are blank, and
//                   every row can be entered); false when `rows` are items.
//   mode            'segmented' (immediate children + drill arrows) or
//                   'collapsed' (flattened descendants, indented, no arrows).
//   hasChildren     (item) => whether a non-root row can be drilled into.
//   depthOf         (item) => indentation depth (collapsed mode only).
//   isExpanded      (item) => whether a parent's subtree is shown (collapsed mode).
//   onOpen(row)     label click: select/open the row (loads into a window later).
//   onForward(row)  arrow click: enter a standard (root) or drill into children.
//   onToggleCollapse(row) chevron click: fold/unfold a subtree (collapsed mode).
//   isBookmarked(row) => whether the row is bookmarked (items only, not root).
//   isDimmed(row) => row is shown only as context (ancestor of a match) when
//                    filtering; rendered muted and not itself a match.
//   onBookmark(row) bookmark-tag click: open the create/edit bookmark modal.
function CodeNav({
  status,
  rows,
  atRoot,
  mode,
  selectedId,
  hasChildren,
  depthOf,
  isExpanded,
  isBookmarked,
  bookmarkTopics,
  isDimmed,
  onOpen,
  onForward,
  onToggleCollapse,
  onBookmark,
  scrollToId,
  scrollNonce,
}) {
  const rootRef = useRef(null)

  // Scroll the revealed row into view when the "jump to section" button fires.
  // Keyed off the nonce so repeated clicks on the same section re-scroll.
  useEffect(() => {
    if (!scrollToId) return
    const el = rootRef.current?.querySelector(`[data-code-nav-id="${scrollToId}"]`)
    el?.scrollIntoView({ block: 'nearest' })
  }, [scrollNonce, scrollToId])

  if (status === 'loading') return <div className="nav-table-status">Loading…</div>
  if (status === 'error') return <div className="nav-table-status">Could not load.</div>
  if (!rows.length) {
    return (
      <div className="nav-table-status">
        {atRoot ? 'No standards available.' : 'Nothing here.'}
      </div>
    )
  }

  return (
    <div className="code-nav" role="tree" aria-label="Library" ref={rootRef}>
      {rows.map((row) => {
        const parent = !atRoot && hasChildren(row)
        // Column 4: at root the arrow enters a standard; segmented parents drill
        // in; collapsed parents get an expand/collapse chevron.
        const showForward = atRoot || (mode === 'segmented' && parent)
        const showChevron = mode === 'collapsed' && parent
        const expanded = showChevron && isExpanded?.(row)
        const depth = depthOf ? depthOf(row) : 0
        // Bookmarks apply to items, not the level-0 standard list.
        const bookmarked = !atRoot && isBookmarked?.(row)
        const dimmed = isDimmed?.(row)
        return (
          <div
            key={row._id}
            data-code-nav-id={row._id}
            className={
              'code-nav-row' +
              (row._id === selectedId ? ' is-selected' : '') +
              (dimmed ? ' is-dimmed' : '')
            }
            style={{ '--code-nav-level': depth }}
            role="treeitem"
            onClick={() => onOpen?.(row)}
          >
            <div className="code-nav-label" title={row.label}>
              {row.label}
            </div>
            <div className="code-nav-page">{atRoot ? '' : row.pageStart ?? ''}</div>
            <div className="code-nav-bookmark">
              {!atRoot && (
                <BookmarkButton
                  bookmarked={bookmarked}
                  topics={bookmarked ? bookmarkTopics?.(row) : []}
                  onClick={() => onBookmark?.(row)}
                />
              )}
            </div>
            <div className="code-nav-forward">
              {showForward && (
                <button
                  type="button"
                  className="code-nav-forward-btn"
                  title={atRoot ? `Browse ${row.label}` : 'See subsections'}
                  aria-label={atRoot ? `Browse ${row.label}` : 'See subsections'}
                  onClick={(event) => {
                    event.stopPropagation()
                    onForward?.(row)
                  }}
                >
                  <IconForward />
                </button>
              )}
              {showChevron && (
                <button
                  type="button"
                  className="code-nav-forward-btn"
                  title={expanded ? 'Collapse' : 'Expand'}
                  aria-label={expanded ? 'Collapse' : 'Expand'}
                  aria-expanded={expanded}
                  onClick={(event) => {
                    event.stopPropagation()
                    onToggleCollapse?.(row)
                  }}
                >
                  <IconChevron up={!expanded} />
                </button>
              )}
            </div>
          </div>
        )
      })}
    </div>
  )
}

export default CodeNav
